import { useState } from 'react';
import { AutocompleteRenderInputParams, Box, FormInputText, InputLabel, Modal } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { useCancelReason } from '@rideiq/shared/hooks/useCancelReason';

import { StyledSelect, StyledTable, Text } from './styles';

type Ride = {
  endsAt: string;
  startsAt: string;
  slug: string;
  pickupLocation: { name?: string; address: string };
  dropoffLocation: { name?: string; address: string };
  riderSlug: string;
  fullName: string;
};

interface IRemoveRiderFromTodaysRidesModal {
  isOpen: boolean;
  formatInTimezone: (date?: Date | string, dateFormat?: string) => string;
  rides: Ride[];
  onAccept: (cancelReasonKey: string) => Promise<void>;
  onCancel: () => void;
}

export const RemoveRiderFromTodaysRidesModal = ({
  isOpen,
  formatInTimezone,
  rides,
  onAccept,
  onCancel,
}: IRemoveRiderFromTodaysRidesModal) => {
  if (!isOpen) {
    return null;
  }

  return <PresentationV2 formatInTimezone={formatInTimezone} rides={rides} onAccept={onAccept} onCancel={onCancel} />;
};
const PresentationV2 = ({ formatInTimezone, rides, onAccept, onCancel }) => {
  const [cancelReasonSelected, setCancelReasonSelected] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const { cancelReasons } = useCancelReason('single');

  const onApply = async () => {
    setIsAccepting(true);
    await onAccept(cancelReasonSelected);
  };

  return (
    <Modal open={true} maxWidth="xl">
      <Modal.Title>{`Would you like to remove these riders from their remaining rides today ${formatInTimezone(
        new Date().toISOString(),
        'MM/dd/yyyy'
      )}?`}</Modal.Title>
      <Modal.Content>
        <Table rides={rides} formatInTimezone={formatInTimezone} />
        <Box display="flex" alignItems="center" gap="2rem" mt="1rem">
          <InputLabel htmlFor="cancelReason">Reason</InputLabel>
          <StyledSelect
            id="cancelReason"
            options={cancelReasons}
            value={cancelReasons.length ? cancelReasonSelected : ''}
            setValue={setCancelReasonSelected}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <FormInputText
                {...params}
                variant="outlined"
                fullWidth
                data-testid="cancelReason"
                placeholder="Select a reason"
              />
            )}
          />
        </Box>
      </Modal.Content>
      <Modal.Actions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button color="tertiary" onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button color="primary" onClick={onApply} disabled={isAccepting || !cancelReasonSelected} loading={isAccepting}>
          Apply
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
const Table = ({
  rides,
  formatInTimezone,
}: {
  rides: Ride[];
  formatInTimezone: (date?: Date | string, dateFormat?: string) => string;
}) => {
  return (
    <StyledTable>
      <tr>
        <th>
          <Text textColor="secondaryText">Rider</Text>
        </th>
        <th>
          <Text textColor="secondaryText">Estimated pick-up time</Text>
        </th>
        <th>
          <Text textColor="secondaryText">Pick-up Location</Text>
        </th>
        <th>
          <Text textColor="secondaryText">Estimated drop-off time</Text>
        </th>
        <th>
          <Text textColor="secondaryText">Drop-off Location</Text>
        </th>
      </tr>
      {rides.map((ride) => {
        const pickupLocation = ride.pickupLocation.name || ride.pickupLocation.address;
        const dropoffLocation = ride.dropoffLocation?.name || ride.dropoffLocation.address;

        return (
          <tr key={`${ride.slug}-${ride.riderSlug}`} data-testid={`${ride.slug}-${ride.riderSlug}`}>
            <td title={ride.fullName}>{ride.fullName}</td>
            <td>{formatInTimezone(ride.startsAt, 'EEE, MM/dd, hh:mm aa')}</td>
            <td title={pickupLocation}>{pickupLocation}</td>
            <td>{formatInTimezone(ride.endsAt, 'EEE, MM/dd, hh:mm aa')}</td>
            <td title={dropoffLocation}>{dropoffLocation}</td>
          </tr>
        );
      })}
    </StyledTable>
  );
};
